import { useContextMenu } from "../lib/useContextMenu";
import { useT } from "../lib/useT";
import { REDACTED_THINKING, type ChatEntry } from "../openclaw/transcript";
import { summarizeSteps } from "../openclaw/turns";
import { ToolChip } from "./MessageBubble";
import { IconMoreHorizontal, IconSteps } from "./icons";

/**
 * The intermediate steps of one assistant turn, collapsed into a single line.
 *
 * Deliberately not bubble-shaped: what the agent did on the way is scaffolding
 * around the reply, not a message of its own. It stays collapsed by default —
 * including while the run is still going, where the counter just climbs — and
 * opens through the menu: long-press on touch, the ⋯ button or a right-click on
 * a desktop pointer.
 */
export function TurnSteps({
  steps,
  expanded,
  live,
  onMenu,
}: {
  steps: ChatEntry[];
  expanded: boolean;
  live: boolean;
  onMenu: () => void;
}) {
  const contextMenu = useContextMenu(onMenu);
  const t = useT();
  const { count, labels } = summarizeSteps(steps, t);

  if (!count) {
    return null;
  }

  return (
    <div className={`turn-steps${expanded ? " is-expanded" : ""}`}>
      <div
        className="turn-steps-head"
        onContextMenu={contextMenu.onContextMenu}
        onPointerDown={contextMenu.onPointerDown}
        onPointerMove={contextMenu.onPointerMove}
        onPointerUp={contextMenu.onPointerUp}
        onPointerCancel={contextMenu.onPointerCancel}
      >
        <span className="turn-steps-icon" aria-hidden="true">
          <IconSteps />
        </span>
        <span className="turn-steps-label">
          <span className="turn-steps-count">
            {count === 1 ? t("steps.one") : t("steps.many", { count })}
          </span>
          {labels.length ? (
            <span className="turn-steps-detail">{labels.join(" · ")}</span>
          ) : null}
        </span>
        {live ? <span className="turn-steps-live">{t("steps.running")}</span> : null}
        <button
          type="button"
          className="turn-steps-menu"
          onClick={onMenu}
          aria-label={t("steps.options")}
          title={t("steps.options")}
        >
          <IconMoreHorizontal size={18} />
        </button>
      </div>

      {expanded ? (
        <div className="turn-steps-body">
          {steps.map((step) => (
            <div className="turn-step" key={step.id}>
              {step.thinking ? (
                <p className="turn-step-thinking">
                  <span className="turn-step-tag">{t("steps.thoughts")}</span>
                  {step.thinking === REDACTED_THINKING
                    ? t("attachment.redactedThinking")
                    : step.thinking}
                </p>
              ) : null}
              {step.toolActivities.length ? (
                <div className="tool-list">
                  {step.toolActivities.map((activity) => (
                    <ToolChip key={activity.id} activity={activity} />
                  ))}
                </div>
              ) : null}
              {step.text ? <p className="turn-step-text">{step.text}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
